import { useState } from "react";
import { CheckCircle2, Circle, Clock, AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { ESTADOS_CICLO } from "../../lib/catalogs";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";

export default function TabResumen({ fideicomiso, completitud, onReload }) {
    const { hasRole } = useAuth();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ nuevo_estado: "activo", motivo: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const canChangeState = hasRole([
        ROLES.DIRECCION,
        ROLES.CUMPLIMIENTO,
        ROLES.JURIDICO,
        ROLES.ADMIN_TI,
    ]);
    const puedeActivar = completitud?.puede_activar && fideicomiso.estado_ciclo === "en_constitucion";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("nuevo_estado", form.nuevo_estado);
            fd.append("motivo", form.motivo);
            if (file) fd.append("evidencia", file);
            await api.post(`/fideicomisos/${fideicomiso.id}/estado`, fd);
            toast.success(`Estado cambiado a ${form.nuevo_estado}`);
            setOpen(false);
            onReload();
        } catch (err) {
            toast.error("No se pudo cambiar", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setBusy(false);
        }
    };

    const pct = completitud?.porcentaje ?? 0;
    const pctColor =
        pct >= 90
            ? "var(--brand-exito)"
            : pct >= 50
              ? "var(--brand-dark-gold)"
              : "var(--brand-coral)";

    return (
        <div className="space-y-6 pb-16">
            {/* Bloqueos KYC */}
            {completitud?.bloqueos_kyc?.length > 0 && (
                <div
                    className="border rounded-md p-4 flex items-start gap-3"
                    style={{
                        borderColor: "var(--brand-coral)",
                        backgroundColor: "rgba(226,83,31,0.05)",
                    }}
                    data-testid="bloqueos-kyc-alert"
                >
                    <ShieldAlert
                        className="h-5 w-5 shrink-0 mt-0.5"
                        style={{ color: "var(--brand-coral)" }}
                    />
                    <div>
                        <div className="font-semibold text-sm">Alerta de KYC bloqueante</div>
                        <div className="text-sm text-muted-foreground mt-1">
                            {completitud.bloqueos_kyc.length} parte(s) con KYC vencido sin
                            override activo. No se pueden emitir instrucciones ni pagos hasta
                            regularizar. (Aplicará en Fase 2.)
                        </div>
                    </div>
                </div>
            )}

            {/* Onboarding checklist */}
            <div className="kpi-card" data-testid="onboarding-checklist">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                        <h2 className="font-display text-2xl">Onboarding del expediente</h2>
                        <p className="text-sm text-muted-foreground">
                            Ítems requeridos para activar el fideicomiso.
                        </p>
                    </div>
                    <div className="text-right">
                        <div
                            className="kpi-value tabular"
                            style={{ color: pctColor }}
                            data-testid="completitud-pct"
                        >
                            {pct}%
                        </div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-widest">
                            {completitud?.completos}/{completitud?.total} completos
                        </div>
                    </div>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden mb-6">
                    <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: pctColor }}
                    />
                </div>

                <ul className="grid md:grid-cols-2 gap-2">
                    {completitud?.items?.map((it) => (
                        <li
                            key={it.key}
                            className={`flex items-start gap-2.5 rounded-sm border px-3 py-2.5 ${
                                it.ok ? "border-border" : "border-border/40"
                            }`}
                            data-testid={`check-${it.key}`}
                        >
                            {it.ok ? (
                                <CheckCircle2
                                    className="h-4 w-4 shrink-0 mt-0.5"
                                    style={{ color: "var(--brand-exito)" }}
                                />
                            ) : it.placeholder ? (
                                <Clock className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                            ) : (
                                <Circle className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                            )}
                            <div className="flex-1">
                                <div
                                    className={`text-sm ${
                                        it.placeholder ? "text-muted-foreground" : ""
                                    }`}
                                >
                                    {it.label}
                                </div>
                                {it.placeholder && (
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                                        Placeholder Fase 2
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>

                {canChangeState && (
                    <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                        <div>
                            <div className="text-sm font-medium">Cambiar estado del ciclo</div>
                            <div className="text-xs text-muted-foreground">
                                Solo Dirección, Cumplimiento, Jurídico o Admin TI.
                            </div>
                        </div>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    variant={puedeActivar ? "default" : "outline"}
                                    data-testid="cambiar-estado-btn"
                                >
                                    Cambiar estado
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Cambio de estado del ciclo</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={submit} className="space-y-3">
                                    <div>
                                        <Label>Nuevo estado</Label>
                                        <Select
                                            value={form.nuevo_estado}
                                            onValueChange={(v) =>
                                                setForm({ ...form, nuevo_estado: v })
                                            }
                                        >
                                            <SelectTrigger data-testid="select-nuevo-estado">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ESTADOS_CICLO.map((e) => (
                                                    <SelectItem key={e.value} value={e.value}>
                                                        {e.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Motivo del cambio</Label>
                                        <Textarea
                                            required
                                            value={form.motivo}
                                            onChange={(e) =>
                                                setForm({ ...form, motivo: e.target.value })
                                            }
                                            data-testid="input-motivo-estado"
                                        />
                                    </div>
                                    <div>
                                        <Label>Evidencia (opcional)</Label>
                                        <Input
                                            type="file"
                                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                                            data-testid="input-evidencia-estado"
                                        />
                                    </div>
                                    {form.nuevo_estado === "activo" && !puedeActivar && (
                                        <div className="text-xs flex items-center gap-2 text-[color:var(--brand-coral)]">
                                            <AlertTriangle className="h-3 w-3" />
                                            Se requiere completitud ≥ 90% para activar.
                                        </div>
                                    )}
                                    <DialogFooter>
                                        <Button
                                            type="submit"
                                            disabled={busy}
                                            data-testid="submit-cambio-estado"
                                        >
                                            {busy ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                "Confirmar cambio"
                                            )}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            {/* Datos base */}
            <div className="kpi-card">
                <h2 className="font-display text-2xl mb-3">Identificación</h2>
                <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <DataRow label="Número interno" value={fideicomiso.numero_interno} />
                    <DataRow label="Número fiduciario" value={fideicomiso.numero_fiduciario} />
                    <DataRow label="Tipo" value={fideicomiso.tipo} />
                    <DataRow label="Fiduciario" value={fideicomiso.fiduciario} />
                    <DataRow label="Constitución" value={fideicomiso.fecha_constitucion} />
                    <DataRow label="Vigencia" value={fideicomiso.vigencia || "—"} />
                    <DataRow label="Notario" value={fideicomiso.notario || "—"} />
                    <DataRow label="Escritura" value={fideicomiso.escritura || "—"} />
                </dl>
            </div>
        </div>
    );
}

function DataRow({ label, value }) {
    return (
        <div className="flex justify-between border-b border-border/50 py-2">
            <dt className="text-muted-foreground text-xs uppercase tracking-widest">{label}</dt>
            <dd className="font-medium tabular">{value}</dd>
        </div>
    );
}
